// FILE: localExecCatalog.ts
// Purpose: Turn the local-command form into catalog entries, and cross a catalog with a device's acceptances.
// Layer: Web domain logic (pure — no React, no window)
// Exports: draft model, draft <-> entry conversion, per-device status resolution

import {
  LOCAL_EXEC_DEFAULT_MAX_OUTPUT_BYTES,
  LOCAL_EXEC_DEFAULT_TIMEOUT_MS,
  LOCAL_EXEC_MAX_ARGS,
  LOCAL_EXEC_MAX_DOWNLOAD_BYTES,
  LOCAL_EXEC_MAX_OUTPUT_BYTES,
  LOCAL_EXEC_MAX_TIMEOUT_MS,
  computeLocalActionHash,
  tokenizeLocalCommand,
  validateProjectLocalAction,
  type LocalActionArgSpec,
  type LocalActionArgType,
  type LocalExecAcceptedEntry,
  type LocalExecApprovalMode,
  type LocalExecOutputMode,
  type LocalExecRunMode,
  type ProjectLocalAction,
} from "@taskin-code/contracts";

const MAX_LOCAL_ACTION_ID_LENGTH = 64;

/** One argument row of the form, all fields as typed text. */
export interface LocalActionArgDraft {
  /**
   * Row identity for React, never sent anywhere. A row has no stable field of
   * its own while it is being filled in — `name` starts empty and changes as
   * the author types — so the list needs an identity the model carries.
   */
  rowId: string;
  name: string;
  type: LocalActionArgType;
  required: boolean;
  /** Comma-separated, only meaningful for `enum`. */
  enumValues: string;
  pattern: string;
  /** Comma-separated extensions, only meaningful for `file` (`.apk, .aab`). */
  fileExtensions: string;
  /** Per-file ceiling in MiB, only meaningful for `file`. Empty = the global cap. */
  maxBytesMib: string;
}

/**
 * The catalog form, one flat record of strings.
 *
 * `command` is a single text field in both kinds: the frozen argv of an
 * `action` (with `{name}` placeholders) or the token pattern of a `pattern`
 * (`adb *`). It is tokenized with the contracts tokenizer, so what the author
 * types here is split exactly the way the agent's command will be.
 */
export interface LocalActionDraft {
  /** Empty while adding; the entry's id while editing. */
  id: string;
  title: string;
  description: string;
  kind: ProjectLocalAction["kind"];
  command: string;
  args: LocalActionArgDraft[];
  approval: LocalExecApprovalMode;
  mode: LocalExecRunMode;
  cwd: string;
  timeoutSeconds: string;
  maxOutputKib: string;
  /** Whether the run also sends a file back to the server. */
  output: LocalExecOutputMode;
}

let nextArgRowId = 0;

export const emptyLocalActionArgDraft = (): LocalActionArgDraft => ({
  rowId: `arg-${(nextArgRowId += 1)}`,
  name: "",
  type: "string",
  required: true,
  enumValues: "",
  pattern: "",
  fileExtensions: "",
  maxBytesMib: "",
});

export const emptyLocalActionDraft = (): LocalActionDraft => ({
  id: "",
  title: "",
  description: "",
  kind: "pattern",
  command: "",
  args: [],
  approval: "always_ask",
  mode: "sync",
  cwd: "",
  timeoutSeconds: String(LOCAL_EXEC_DEFAULT_TIMEOUT_MS / 1_000),
  maxOutputKib: String(LOCAL_EXEC_DEFAULT_MAX_OUTPUT_BYTES / 1_024),
  output: "text",
});

/**
 * Render tokens back as one line. Only quoting is reconstructed: a token that
 * carries whitespace or a quote is wrapped, everything else is printed as it
 * is, so `adb *` round-trips through the form unchanged.
 */
export const formatLocalCommandTokens = (tokens: ReadonlyArray<string>): string =>
  tokens
    .map((token) => {
      if (token.length === 0) return '""';
      if (!/[\s"']/.test(token)) return token;
      return token.includes('"') ? `'${token}'` : `"${token}"`;
    })
    .join(" ");

export const localActionCommandText = (entry: ProjectLocalAction): string =>
  formatLocalCommandTokens(entry.kind === "pattern" ? entry.pattern : entry.argv);

export function draftFromLocalAction(entry: ProjectLocalAction): LocalActionDraft {
  return {
    id: entry.id,
    title: entry.title,
    description: entry.description ?? "",
    kind: entry.kind,
    command: localActionCommandText(entry),
    args:
      entry.kind === "action"
        ? entry.args.map((spec) => ({
            rowId: `arg-${(nextArgRowId += 1)}`,
            name: spec.name,
            type: spec.type,
            required: spec.required,
            enumValues: (spec.enumValues ?? []).join(", "),
            pattern: spec.pattern ?? "",
            fileExtensions: (spec.fileExtensions ?? []).join(", "),
            maxBytesMib:
              spec.maxBytes === undefined ? "" : String(Math.round(spec.maxBytes / 1_048_576)),
          }))
        : [],
    approval: entry.approval,
    mode: entry.mode,
    cwd: entry.cwd ?? "",
    timeoutSeconds: String(Math.round(entry.timeoutMs / 1_000)),
    maxOutputKib: String(Math.round(entry.maxOutputBytes / 1_024)),
    output: entry.output,
  };
}

/**
 * A template as a form to fill in: the same fields, minus the identity.
 *
 * Blanking the id is what makes this a copy rather than an edit — the new
 * entry gets an id derived from its own title, so the source stays untouched
 * and both can live in their projects at once. `cwd` comes across as it is,
 * which is the field the author most likely has to change: it is a path on
 * whoever wrote the template's machine.
 */
export function draftFromLocalActionTemplate(entry: ProjectLocalAction): LocalActionDraft {
  return { ...draftFromLocalAction(entry), id: "" };
}

function normalizeLocalActionId(value: string): string {
  const cleaned = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (cleaned.length === 0) return "comando";
  if (cleaned.length <= MAX_LOCAL_ACTION_ID_LENGTH) return cleaned;
  return cleaned.slice(0, MAX_LOCAL_ACTION_ID_LENGTH).replace(/-+$/g, "") || "comando";
}

/** A stable id derived from the title, made unique against `existingIds`. */
export function nextLocalActionId(title: string, existingIds: Iterable<string>): string {
  const taken = new Set(Array.from(existingIds));
  const baseId = normalizeLocalActionId(title);
  if (!taken.has(baseId)) return baseId;

  for (let suffix = 2; suffix < 10_000; suffix += 1) {
    const candidate = `${baseId}-${suffix}`;
    const safeCandidate =
      candidate.length <= MAX_LOCAL_ACTION_ID_LENGTH
        ? candidate
        : `${baseId.slice(0, Math.max(1, MAX_LOCAL_ACTION_ID_LENGTH - String(suffix).length - 1))}-${suffix}`;
    if (!taken.has(safeCandidate)) return safeCandidate;
  }
  return `${baseId}-${Date.now()}`.slice(0, MAX_LOCAL_ACTION_ID_LENGTH);
}

export type LocalActionBuildResult =
  | { readonly ok: true; readonly entry: ProjectLocalAction }
  | { readonly ok: false; readonly reason: string };

function parseBoundedInt(
  raw: string,
  label: string,
  min: number,
  max: number,
): { ok: true; value: number } | { ok: false; reason: string } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: false, reason: `Falta ${label}.` };
  const value = Number(trimmed);
  if (!Number.isInteger(value)) return { ok: false, reason: `${label} tiene que ser un entero.` };
  if (value < min || value > max) {
    return { ok: false, reason: `${label} tiene que estar entre ${min} y ${max}.` };
  }
  return { ok: true, value };
}

function buildArgSpecs(
  drafts: ReadonlyArray<LocalActionArgDraft>,
): { ok: true; args: LocalActionArgSpec[] } | { ok: false; reason: string } {
  if (drafts.length > LOCAL_EXEC_MAX_ARGS) {
    return { ok: false, reason: `Como mucho ${LOCAL_EXEC_MAX_ARGS} argumentos.` };
  }

  const args: LocalActionArgSpec[] = [];
  for (const draft of drafts) {
    const name = draft.name.trim();
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name)) {
      return {
        ok: false,
        reason: `'${name || "(sin nombre)"}' no es un nombre de argumento válido (letras, dígitos y _, empezando por letra).`,
      };
    }
    const enumValues = draft.enumValues
      .split(",")
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
    const pattern = draft.pattern.trim();

    if (draft.type === "file") {
      // The two narrowings a file argument takes, and the reason each one is
      // worth typing: the extension list is what stops an agent from sending
      // something other than the artifact this command expects, and the size
      // cap is what stops it from pushing a 300 MiB blob at a laptop.
      const fileExtensions = draft.fileExtensions
        .split(",")
        .map((value) => value.trim().toLowerCase())
        .filter((value) => value.length > 0)
        .map((value) => (value.startsWith(".") ? value : `.${value}`));
      const invalid = fileExtensions.find((value) => !/^\.[a-z0-9]{1,10}$/.test(value));
      if (invalid !== undefined) {
        return {
          ok: false,
          reason: `'${invalid}' no es una extensión válida (por ejemplo: .apk, .aab).`,
        };
      }
      if (fileExtensions.length > 8) {
        return { ok: false, reason: "Como mucho 8 extensiones por argumento." };
      }

      const rawMaxBytes = draft.maxBytesMib.trim();
      let maxBytes: number | undefined;
      if (rawMaxBytes.length > 0) {
        const parsed = parseBoundedInt(
          rawMaxBytes,
          `el tamaño máximo de '${name}' (MiB)`,
          1,
          Math.floor(LOCAL_EXEC_MAX_DOWNLOAD_BYTES / 1_048_576),
        );
        if (!parsed.ok) return { ok: false, reason: parsed.reason };
        maxBytes = parsed.value * 1_048_576;
      }

      args.push({
        name,
        type: "file",
        required: draft.required,
        ...(fileExtensions.length > 0 ? { fileExtensions } : {}),
        ...(maxBytes === undefined ? {} : { maxBytes }),
      });
      continue;
    }

    args.push({
      name,
      type: draft.type,
      required: draft.required,
      ...(draft.type === "enum" ? { enumValues } : {}),
      ...(pattern.length > 0 ? { pattern } : {}),
    });
  }
  return { ok: true, args };
}

/**
 * Build the entry the server will be sent, or the reason it cannot be built.
 *
 * Everything the schema and `validateProjectLocalAction` would refuse is caught
 * here first, so an author sees "'**' sólo puede ir como último token" in the
 * form instead of a rejected command in a toast.
 */
export function buildLocalActionFromDraft(
  draft: LocalActionDraft,
  existingIds: Iterable<string>,
): LocalActionBuildResult {
  const title = draft.title.trim();
  if (title.length === 0) return { ok: false, reason: "Ponle un nombre al comando." };
  if (title.length > 120)
    return { ok: false, reason: "El nombre no puede pasar de 120 caracteres." };

  const description = draft.description.trim();
  if (description.length > 500) {
    return { ok: false, reason: "La descripción no puede pasar de 500 caracteres." };
  }

  const tokenized = tokenizeLocalCommand(draft.command);
  if (!tokenized.ok) return { ok: false, reason: tokenized.reason };
  const [head, ...rest] = tokenized.tokens;
  if (head === undefined) return { ok: false, reason: "El comando está vacío." };

  const timeout = parseBoundedInt(
    draft.timeoutSeconds,
    "el tiempo máximo (segundos)",
    1,
    LOCAL_EXEC_MAX_TIMEOUT_MS / 1_000,
  );
  if (!timeout.ok) return { ok: false, reason: timeout.reason };

  const maxOutput = parseBoundedInt(
    draft.maxOutputKib,
    "la salida máxima (KiB)",
    1,
    LOCAL_EXEC_MAX_OUTPUT_BYTES / 1_024,
  );
  if (!maxOutput.ok) return { ok: false, reason: maxOutput.reason };

  const cwd = draft.cwd.trim();
  if (cwd.length > 0 && !cwd.startsWith("/")) {
    return { ok: false, reason: "El directorio tiene que ser una ruta absoluta." };
  }

  const id = draft.id.trim().length > 0 ? draft.id.trim() : nextLocalActionId(title, existingIds);

  const base = {
    id,
    title,
    ...(description.length > 0 ? { description } : {}),
    approval: draft.approval,
    cwd: cwd.length > 0 ? cwd : null,
    timeoutMs: timeout.value * 1_000,
    maxOutputBytes: maxOutput.value * 1_024,
    mode: draft.mode,
    output: draft.output,
    // Never authored here. Whether an entry is offered as a template in other
    // projects is published with a different grant, and the decider carries the
    // stored value forward by id — so a constant `false` is both what the form
    // knows and what the server will ignore. It is also what makes "start from
    // a template" safe: the copy cannot arrive already published.
    isTemplate: false,
  } as const;

  let entry: ProjectLocalAction;
  if (draft.kind === "pattern") {
    entry = { ...base, kind: "pattern", pattern: [head, ...rest] };
  } else {
    const args = buildArgSpecs(draft.args);
    if (!args.ok) return { ok: false, reason: args.reason };
    entry = { ...base, kind: "action", argv: [head, ...rest], args: args.args };
  }

  const validation = validateProjectLocalAction(entry);
  if (!validation.ok) return { ok: false, reason: validation.reason };
  return { ok: true, entry };
}

/**
 * Where a catalog entry stands on *this* device.
 *
 * `changed` is the case worth naming: the entry was accepted here, but its
 * security fields moved server-side, so the device will refuse it until its
 * owner looks at the new command and accepts again.
 */
export type LocalActionDeviceStatus = "accepted" | "changed" | "not-accepted";

export function resolveLocalActionDeviceStatus(
  entry: ProjectLocalAction,
  accepted: ReadonlyArray<LocalExecAcceptedEntry>,
): LocalActionDeviceStatus {
  const record = accepted.find((candidate) => candidate.entryId === entry.id);
  if (!record) return "not-accepted";
  return record.hash === computeLocalActionHash(entry) ? "accepted" : "changed";
}

export const LOCAL_ACTION_DEVICE_STATUS_LABEL: Record<LocalActionDeviceStatus, string> = {
  accepted: "Aceptado",
  changed: "Cambió",
  "not-accepted": "No aceptado",
};

/**
 * Acceptances left over from entries the catalog no longer has (deleted
 * server-side, or belonging to a project this user no longer sees). They are
 * dead weight on the device, so the UI offers to revoke them.
 */
export function orphanAcceptedEntries(
  accepted: ReadonlyArray<LocalExecAcceptedEntry>,
  catalog: ReadonlyArray<ProjectLocalAction>,
): ReadonlyArray<LocalExecAcceptedEntry> {
  const known = new Set(catalog.map((entry) => entry.id));
  return accepted.filter((record) => !known.has(record.entryId));
}
